import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { UserListItem } from '#/shared/api/generated/auth/models'
import { useListUsersApiV1UsersGet } from '#/shared/api/generated/auth/theCargoAuth'
import type {
  ShipmentDetail,
  UserBrief,
} from '#/shared/api/generated/shipment/models'
import { useUpdateShipmentApiV1ShipmentsShipmentIdPatch } from '#/shared/api/generated/shipment/theCargoShipment'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { Button } from '#/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '#/shared/ui/dialog'
import { toast } from '#/shared/ui/sonner'
import { UserDropdown } from '#/shared/ui/user-dropdown'
import type { UserDropdownProps, UserOption } from '#/shared/ui/user-dropdown'

import { invalidateShipmentLists } from '../lib/invalidate-shipment-queries'

export const FALLBACK_USER: UserOption = { id: '0', name: '—' }
const USERS_PAGE_SIZE = 100

export function toUserOption(user: UserListItem): UserOption {
  const fullName = [user.first_name, user.last_name]
    .filter(Boolean)
    .join(' ')
    .trim()
  const name = fullName.length > 0 ? fullName : user.email
  return {
    id: user.id,
    name,
    avatarUrl: user.picture?.url ?? undefined,
  }
}

export function assignedOwnerOption(
  user: UserBrief | null | undefined,
): UserOption | undefined {
  if (!user) return undefined
  const fullName = [user.first_name, user.last_name]
    .filter(Boolean)
    .join(' ')
    .trim()
  const name = fullName.length > 0 ? fullName : (user.email ?? '')
  if (!name) return undefined
  return { id: user.id, name }
}

export function useShipmentUserOptions(shipment: ShipmentDetail | undefined): {
  users: UserOption[]
  currentUser: UserOption
} {
  const usersQuery = useListUsersApiV1UsersGet({ size: USERS_PAGE_SIZE })
  const users = useMemo<UserOption[]>(
    () => (usersQuery.data?.data.items ?? []).map(toUserOption),
    [usersQuery.data],
  )
  // `shipment.user` is a UserBrief without a picture, so the owner option has
  // no avatar. Pull it from the full users list (UserListItem has the picture)
  // by id so the trigger label shows the owner's avatar, not just initials.
  const currentUser = useMemo<UserOption>(() => {
    const owner = assignedOwnerOption(shipment?.user) ?? users.at(0)
    if (!owner) return FALLBACK_USER
    if (owner.avatarUrl) return owner
    const match = users.find((u) => u.id === owner.id)
    return match?.avatarUrl ? { ...owner, avatarUrl: match.avatarUrl } : owner
  }, [shipment?.user, users])
  return { users, currentUser }
}

function useReassignOwner(
  shipment: ShipmentDetail | undefined,
  currentUserId: string,
  onUserChange: ((user: UserOption) => void) | undefined,
) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const reassign = useUpdateShipmentApiV1ShipmentsShipmentIdPatch({
    mutation: {
      onSuccess: (_response, variables) => {
        invalidateShipmentLists(queryClient)
        queryClient.invalidateQueries({
          queryKey: [`/api/v1/shipments/${variables.shipmentId}`],
        })
        toast.success(t('detailToolbar.reassignSuccess'))
      },
      onError: (error) => {
        toast.error(
          getApiErrorDetail(error) ?? t('detailToolbar.reassignError'),
        )
      },
    },
  })

  return (user: UserOption) => {
    onUserChange?.(user)
    if (!shipment?.id) return
    if (user.id === currentUserId) return
    reassign.mutate({ shipmentId: shipment.id, data: { user_id: user.id } })
  }
}

export function useReassignFlow(
  shipment: ShipmentDetail | undefined,
  currentUserId: string,
  onUserChange: ((user: UserOption) => void) | undefined,
) {
  const reassignOwner = useReassignOwner(shipment, currentUserId, onUserChange)
  const [pendingReassign, setPendingReassign] = useState<UserOption | null>(
    null,
  )
  const requestReassign = (user: UserOption) => {
    if (user.id === currentUserId) return
    setPendingReassign(user)
  }
  const confirmReassign = () => {
    if (!pendingReassign) return
    reassignOwner(pendingReassign)
    setPendingReassign(null)
  }
  const cancelReassign = () => setPendingReassign(null)
  return { pendingReassign, requestReassign, confirmReassign, cancelReassign }
}

export function ReassignConfirmDialog({
  target,
  onCancel,
  onConfirm,
}: {
  target: UserOption | null
  onCancel: () => void
  onConfirm: () => void
}) {
  const { t } = useTranslation()
  return (
    <Dialog
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) onCancel()
      }}
    >
      <DialogContent className="max-w-sm">
        <div className="flex flex-col gap-4 p-5">
          <div className="flex flex-col gap-1.5">
            <DialogTitle>
              {t('detailToolbar.reassignConfirm.title')}
            </DialogTitle>
            <DialogDescription>
              {t('detailToolbar.reassignConfirm.description', {
                name: target?.name ?? '',
              })}
            </DialogDescription>
          </div>
          <div className="flex items-center justify-end gap-2.5">
            <Button
              type="button"
              variant="outline"
              size="xs"
              pressEffect
              className="px-3.5"
              onClick={onCancel}
            >
              {t('detailToolbar.reassignConfirm.cancel')}
            </Button>
            <Button
              type="button"
              size="xs"
              pressEffect
              className="px-3.5"
              onClick={onConfirm}
            >
              {t('detailToolbar.reassignConfirm.confirm')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface ShipmentOwnerDropdownProps {
  shipment: ShipmentDetail | undefined
  side?: UserDropdownProps['side']
  align?: UserDropdownProps['align']
}

export function ShipmentOwnerDropdown({
  shipment,
  side,
  align,
}: ShipmentOwnerDropdownProps) {
  const { users, currentUser } = useShipmentUserOptions(shipment)
  const reassign = useReassignFlow(shipment, currentUser.id, undefined)

  return (
    <>
      <UserDropdown
        currentUser={currentUser}
        users={users}
        onSelect={reassign.requestReassign}
        side={side}
        align={align}
      />
      <ReassignConfirmDialog
        target={reassign.pendingReassign}
        onCancel={reassign.cancelReassign}
        onConfirm={reassign.confirmReassign}
      />
    </>
  )
}
